/**
 * OpenAI Speech-to-Text Hook
 *
 * Manages audio recording and transcription using OpenAI Whisper API
 * - MediaRecorder for browser audio capture
 * - Chromium-optimized (audio/webm;codecs=opus)
 * - No audio storage (streams directly to API)
 * - HIPAA compliant
 */

import { useCallback, useRef, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-openai-stt');
import { useTranscribeSTTMutation } from './use-gateway-queries';

export type STTRecordingState = 'idle' | 'recording' | 'processing' | 'error' | 'success';

export interface UseOpenAISTTOptions {
  /**
   * ISO-639-1 language code (e.g., 'en', 'es')
   * @default undefined (auto-detect)
   */
  language?: string;

  /**
   * Context prompt to guide transcription
   * @default undefined
   */
  prompt?: string;

  /**
   * Sampling temperature (0-1)
   * @default undefined
   */
  temperature?: number;

  /**
   * Callback when transcription completes successfully
   */
  onTranscriptionComplete?: (text: string) => void;

  /**
   * Callback when error occurs
   */
  onError?: (error: Error) => void;
}

export interface UseOpenAISTTResult {
  /**
   * Current recording/processing state
   */
  state: STTRecordingState;

  /**
   * Last transcribed text
   */
  transcribedText: string;

  /**
   * Error if any occurred
   */
  error: Error | null;

  /**
   * Start recording audio
   */
  startRecording: () => Promise<void>;

  /**
   * Stop recording and transcribe
   */
  stopRecording: () => Promise<void>;

  /**
   * Cancel recording without transcribing
   */
  cancelRecording: () => void;

  /**
   * Reset state and clear text
   */
  reset: () => void;

  /**
   * Active MediaStream (for visualization)
   */
  stream: MediaStream | null;

  /**
   * Convenience flags
   */
  isRecording: boolean;
  isProcessing: boolean;
  isIdle: boolean;
}

/**
 * Hook for recording audio and transcribing with OpenAI Whisper
 */
export const useOpenAISTT = (options: UseOpenAISTTOptions = {}): UseOpenAISTTResult => {
  const { language, prompt, temperature, onTranscriptionComplete, onError } = options;

  const [state, setState] = useState<STTRecordingState>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [transcribedText, setTranscribedText] = useState<string>('');

  // Refs for MediaRecorder management
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Mutation for API call
  const transcribeMutation = useTranscribeSTTMutation();

  /**
   * Clean up media stream and recorder
   */
  const cleanup = useCallback(() => {
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Clear recorder reference
    mediaRecorderRef.current = null;

    // Clear audio chunks
    audioChunksRef.current = [];
  }, []);

  /**
   * Handle errors
   */
  const handleError = useCallback(
    (err: Error) => {
      logger.error('❌ [useOpenAISTT] Error:', err);
      setError(err);
      setState('error');
      cleanup();
      onError?.(err);
    },
    [cleanup, onError]
  );

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    try {
      logger.info('🎤 [useOpenAISTT] Starting recording...');

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono
          sampleRate: 16000, // 16kHz optimal for Whisper
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Create MediaRecorder with Chromium-optimized settings
      const mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error(`MIME type ${mimeType} not supported`);
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000, // 128kbps
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Collect audio chunks
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          logger.info('🎤 [useOpenAISTT] Audio chunk collected:', {
            size: event.data.size,
            totalChunks: audioChunksRef.current.length,
          });
        }
      };

      // Start recording
      mediaRecorder.start(100); // Collect chunks every 100ms
      setState('recording');
      setError(null);

      logger.info('✅ [useOpenAISTT] Recording started');
    } catch (err) {
      handleError(err instanceof Error ? err : new Error('Failed to start recording'));
    }
  }, [handleError]);

  /**
   * Stop recording and transcribe
   */
  const stopRecording = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      if (!mediaRecorderRef.current) {
        logger.warn('⚠️ [useOpenAISTT] No active recording to stop');
        resolve();
        return;
      }

      const mediaRecorder = mediaRecorderRef.current;

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        logger.info('🎤 [useOpenAISTT] Recording stopped, processing...');

        try {
          // Create blob from chunks using the actual recorder mimeType when available
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mediaRecorder.mimeType || 'audio/webm',
          });

          logger.info('🎤 [useOpenAISTT] Audio blob created:', {
            sizeKB: (audioBlob.size / 1024).toFixed(2),
            type: audioBlob.type,
          });

          // Clean up stream
          cleanup();

          // Set processing state
          setState('processing');

          // Convert blob to base64
          const arrayBuffer = await audioBlob.arrayBuffer();
          const base64Audio = btoa(
            new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );

          logger.info('🎤 [useOpenAISTT] Sending to API for transcription...');

          // Call API
          const response = await transcribeMutation.mutateAsync({
            body: {
              audio: base64Audio,
              mimeType: audioBlob.type,
              language,
              prompt,
              temperature,
            },
          });

          if (response.status !== 200) {
            throw new Error('Transcription failed');
          }

          const text = response.body.text;
          setTranscribedText(text);
          setState('success');
          setTimeout(() => {
            // Reset state after 3 seconds to allow user to record again
            setState('idle');
          }, 3000);

          logger.info('✅ [useOpenAISTT] Transcription successful:', {
            textLength: text.length,
            language: response.body.language || 'auto-detected',
          });

          // Track analytics
          import('../services/analytics').then(({ mixpanel }) => {
            mixpanel.track('Audio Transcribed to Text', {
              textLength: text.length,
              audioSizeKB: (audioBlob.size / 1024).toFixed(2),
              language: language || 'auto-detect',
            });
          });

          onTranscriptionComplete?.(text);
          resolve();
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Transcription failed');
          handleError(error);
          reject(error);
        }
      };

      // Stop the recorder
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
    });
  }, [
    language,
    prompt,
    temperature,
    cleanup,
    handleError,
    transcribeMutation,
    onTranscriptionComplete,
  ]);

  /**
   * Cancel recording without transcribing
   */
  const cancelRecording = useCallback(() => {
    logger.info('🎤 [useOpenAISTT] Canceling recording...');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Remove onstop handler to prevent transcription
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    cleanup();
    setState('idle');
    setError(null);

    logger.info('✅ [useOpenAISTT] Recording canceled');
  }, [cleanup]);

  /**
   * Reset state and clear text
   */
  const reset = useCallback(() => {
    cleanup();
    setState('idle');
    setError(null);
    setTranscribedText('');
  }, [cleanup]);

  return {
    state,
    transcribedText,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    reset,
    stream: streamRef.current,
    isRecording: state === 'recording',
    isProcessing: state === 'processing',
    isIdle: state === 'idle',
  };
};
